import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { isNil } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { updateInventoryReorderConfigApi } from 'api/inventory';
import { handleError } from 'store/thunks/errorHandlerThunks';

const toFieldValue = (value) => (isNil(value) ? '' : String(value));

const ReorderConfigDialog = ({
  open,
  inventoryItemId,
  ndc,
  drugName,
  facilityName,
  lowStockPercent,
  criticalStockPercent,
  leadTime,
  onSaveSuccess,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [lowStock, setLowStock] = useState('');
  const [criticalStock, setCriticalStock] = useState('');
  const [lead, setLead] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-seed the form with the item's current config each time the dialog opens.
  useEffect(() => {
    if (open) {
      setLowStock(toFieldValue(lowStockPercent));
      setCriticalStock(toFieldValue(criticalStockPercent));
      setLead(toFieldValue(leadTime));
      setSubmitted(false);
    }
  }, [open, lowStockPercent, criticalStockPercent, leadTime]);

  const lowNum = parseFloat(lowStock);
  const criticalNum = parseFloat(criticalStock);
  const leadNum = parseFloat(lead);

  const lowValid = lowStock !== '' && !Number.isNaN(lowNum) && lowNum > 0 && lowNum <= 100;
  const leadValid = lead !== '' && !Number.isNaN(leadNum) && leadNum >= 0;
  // criticalStockPercent is optional; when provided it must be 0–low%.
  const criticalProvided = criticalStock !== '';
  const criticalValid =
    !criticalProvided ||
    (!Number.isNaN(criticalNum) && criticalNum >= 0 && (!lowValid || criticalNum <= lowNum));

  const canSave = lowValid && leadValid && criticalValid;

  let lowHelper = 'Below this % of reorder qty the item is flagged LOW. Required (1–100).';
  if (submitted && lowStock === '') lowHelper = 'Low stock % is required';
  else if (lowStock !== '' && !lowValid) lowHelper = 'Enter a value between 1 and 100';

  let criticalHelper = 'Optional — must be ≤ Low Stock %. Defaults to 0.';
  if (criticalProvided && !criticalValid) criticalHelper = 'Must be between 0 and Low Stock %';

  let leadHelper = 'Supplier lead time in days. Required.';
  if (submitted && lead === '') leadHelper = 'Lead time is required';
  else if (lead !== '' && !leadValid) leadHelper = 'Enter a value of 0 or more';

  const handleClose = () => {
    setSubmitted(false);
    onClose();
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!canSave) return;
    setSaving(true);
    const input = {
      inventoryItemId,
      lowStockPercent: lowNum,
      leadTime: leadNum,
      ...(criticalProvided ? { criticalStockPercent: criticalNum } : {}),
    };
    updateInventoryReorderConfigApi(input)
      .then(() => {
        setSaving(false);
        handleClose();
        onSaveSuccess();
        enqueueSnackbar(`Reorder config updated for ${drugName}`, { variant: 'success' });
      })
      .catch((error) => {
        setSaving(false);
        dispatch(handleError({ error }));
      });
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { width: 520, maxWidth: 520 } }}>
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.25,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          Edit Reorder Config
        </Typography>
        <IconButton onClick={handleClose} size='small' sx={{ mt: -0.25, ml: 1 }}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      {/* ── Drug Identity Strip ── */}
      <Box sx={{ px: 2.5, pb: 1.5 }}>
        <Typography
          variant='subtitle1'
          sx={{ fontWeight: 700, lineHeight: 1.4, color: 'text.primary' }}
        >
          {drugName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Typography variant='body2' color='text.secondary'>
            NDC :
          </Typography>
          <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
            {ndc}
          </Typography>
          <Typography variant='body2' color='text.disabled' sx={{ mx: 0.5 }}>
            |
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Facility :
          </Typography>
          <Typography variant='body2'>{facilityName}</Typography>
        </Box>
      </Box>

      <Divider />

      {/* ── Fields ── */}
      <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity='info' icon={<InfoOutlinedIcon fontSize='small' />} sx={{ py: 0.5 }}>
          These thresholds drive the Low Stock and Critical alerts for this item at this facility.
        </Alert>

        <TextField
          type='number'
          label='Low Stock % *'
          size='small'
          fullWidth
          value={lowStock}
          onChange={(e) => setLowStock(e.target.value)}
          inputProps={{ min: 1, max: 100, step: 1 }}
          error={(submitted && lowStock === '') || (lowStock !== '' && !lowValid)}
          helperText={lowHelper}
        />

        <TextField
          type='number'
          label='Critical Stock %'
          size='small'
          fullWidth
          value={criticalStock}
          onChange={(e) => setCriticalStock(e.target.value)}
          inputProps={{ min: 0, max: 100, step: 1 }}
          error={criticalProvided && !criticalValid}
          helperText={criticalHelper}
        />

        <TextField
          type='number'
          label='Lead Time (days) *'
          size='small'
          fullWidth
          value={lead}
          onChange={(e) => setLead(e.target.value)}
          inputProps={{ min: 0, step: 1 }}
          error={(submitted && lead === '') || (lead !== '' && !leadValid)}
          helperText={leadHelper}
        />
      </Box>

      {/* ── Footer ── */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'flex-end',
        }}
      >
        <Button onClick={handleClose} color='inherit'>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={!canSave || saving}
          endIcon={
            saving ? (
              <CircularProgress size={14} color='inherit' />
            ) : (
              <ArrowForwardIcon fontSize='small' />
            )
          }
        >
          {saving ? 'Saving…' : 'Save Config'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ReorderConfigDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  inventoryItemId: PropTypes.string,
  ndc: PropTypes.string,
  drugName: PropTypes.string,
  facilityName: PropTypes.string,
  lowStockPercent: PropTypes.number,
  criticalStockPercent: PropTypes.number,
  leadTime: PropTypes.number,
  onSaveSuccess: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};
ReorderConfigDialog.defaultProps = {
  inventoryItemId: '',
  ndc: '',
  drugName: '',
  facilityName: '',
  lowStockPercent: null,
  criticalStockPercent: null,
  leadTime: null,
  onSaveSuccess: () => {},
};

export default ReorderConfigDialog;
